import { toast as sonner } from "sonner";

/**
 * First-party toast wrapper over sonner so the app never imports sonner directly — tone is
 * consistent and the backing library is swappable in one place.
 *
 *   const toast = useToast();
 *   toast.success("Saved");
 *   toast.error("Couldn't save");
 *   toast.undo("Deleted", () => restore());        // action toast with an Undo
 *   toast.promise(api.post(...), { loading, success, error });
 */
const DEFAULTS = {
  actionLabel: "Undo",
  dismissLabel: "Dismiss",
  undoErrorMessage: "Couldn't undo — please try again.",
  promiseLoading: "Working…",
  promiseSuccess: "Done",
  promiseError: "Something went wrong — please try again.",
};

const ERROR_DURATION = 8000;

// TOAST-422 (2026-08-31): this returned the value UNCHANGED for anything that was not an object
// carrying a string `message` — and a FastAPI validation error's `detail` is an ARRAY
// ([{type, loc, msg, input}, ...]). That array reached sonner, which renders it as a JSX child, so
// React threw "Objects are not valid as a React child". <Toaster> lives under the single root
// <ErrorBoundary> (App.js), so the throw replaced the ENTIRE APPLICATION and destroyed every
// unsaved form — triggered by something as ordinary as pasting an over-long bank narration into the
// payment reference box, i.e. at the fee counter, mid-collection.
//
// 327 call sites pass `e.response?.data?.detail` straight in, so the guarantee has to live HERE
// rather than at the call sites: nothing but a string may ever reach the toast renderer. Logic
// mirrors lib/api.js formatApiErrorDetail; kept self-contained to avoid an import cycle.
const displayMessage = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value))
    return value
      .map((entry) =>
        typeof entry === "string" ? entry
          : entry && typeof entry.msg === "string" ? entry.msg
          : entry && typeof entry.message === "string" ? entry.message
          : "")
      .filter(Boolean)
      .join(" ") || "The details you entered could not be accepted. Please check and try again.";
  if (typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (typeof value.msg === "string") return value.msg;
    if (typeof value.detail === "string") return value.detail;
    if (Array.isArray(value.detail)) return displayMessage(value.detail);
    if (typeof value.code === "string")
      return value.code.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
    // A React element is a valid child; anything else must not reach the renderer.
    if (value.$$typeof) return value;
    return "The request could not be completed. Please try again.";
  }
  return String(value);
};

const toMessage = (err, fallback) =>
  err?.userMessage || err?.response?.data?.detail?.message || err?.response?.data?.message || err?.message || fallback;

// Every variant is guarded, not just `error`. A 422 `detail` array crashes the renderer exactly
// the same way whether it arrives via error() or info(), and callers do pass server payloads to
// the non-error variants (a "saved" toast echoing a server message, a warning built from
// `data.detail`). Guarding only the error path left the same application-wiping crash reachable
// through four other doors.
export const notify = {
  success: (msg, opts) => sonner.success(displayMessage(msg), opts),
  // Errors linger and announce assertively so they aren't missed (WCAG 4.1.3); callers can override.
  error: (msg, opts = {}) =>
    sonner.error(displayMessage(msg), { duration: ERROR_DURATION, important: true, ...opts }),
  // Persistent variant for critical failures that must not auto-dismiss.
  errorPersistent: (msg, opts = {}) =>
    sonner.error(displayMessage(msg), { duration: Infinity, important: true, ...opts }),
  warning: (msg, opts) => sonner.warning(displayMessage(msg), opts),
  // Distinct info affordance (icon/role) instead of a plain neutral message.
  info: (msg, opts) => sonner.info(displayMessage(msg), opts),
  message: (msg, opts) => sonner(displayMessage(msg), opts),
  loading: (msg, opts) => sonner.loading(displayMessage(msg), opts),
  promise: (p, msgs = {}) => {
    const {
      loading = DEFAULTS.promiseLoading,
      success = DEFAULTS.promiseSuccess,
      error = (err) => toMessage(err, DEFAULTS.promiseError),
      ...rest
    } = msgs;
    // sonner resolves a function form with the settled value, so the guard has to wrap the
    // RESULT — a `success: (data) => data.detail` caller is the same crash one await later.
    const guard = (v) => (typeof v === "function" ? (...a) => displayMessage(v(...a)) : displayMessage(v));
    return sonner.promise(p, {
      loading: displayMessage(loading), success: guard(success), error: guard(error), ...rest });
  },
  dismiss: (id) => sonner.dismiss(id),
  /**
   * Success toast with a one-tap Undo (the workhorse for optimistic delete/change).
   * It persists until the user acts or dismisses it (WCAG 2.2.1 — never race a recovery
   * action), awaits the async restore, and surfaces an assertive error if the restore fails
   * so data loss is never silent. Pass actionLabel/dismissLabel/undoErrorMessage to localise.
   */
  undo: (msg, onUndo, opts = {}) => {
    const {
      actionLabel = DEFAULTS.actionLabel,
      dismissLabel = DEFAULTS.dismissLabel,
      undoErrorMessage = DEFAULTS.undoErrorMessage,
      duration,
      ...toastOpts
    } = opts;
    let id;
    id = sonner.success(msg, {
      ...toastOpts,
      duration: duration ?? Infinity,
      action: {
        label: actionLabel,
        onClick: async (event) => {
          event?.preventDefault?.();
          try {
            await onUndo?.();
            sonner.dismiss(id);
          } catch (err) {
            sonner.error(toMessage(err, undoErrorMessage), {
              id: `${id}-undo-error`,
              duration: ERROR_DURATION,
              important: true,
            });
          }
        },
      },
      cancel: { label: dismissLabel },
    });
    return id;
  },
};

export function useToast() {
  return notify;
}
